import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Send, MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from '../contexts/BusinessContext';
import { db } from '../config/firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { Loading } from '../components/ui/Loading';

interface ChatMessage {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  senderRole: 'user' | 'admin';
  createdAt: Date;
  isRead: boolean;
}

const HablanosPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { businessId, currentBusiness } = useBusiness();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll al final cuando hay nuevos mensajes
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Escuchar mensajes en tiempo real
  useEffect(() => {
    if (!currentUser?.id || !businessId) {
      setLoading(false);
      return;
    }

    // Consulta simplificada mientras se construye el índice
    const chatQuery = query(
      collection(db, 'chats'),
      where('participantIds', 'array-contains', currentUser.id),
      where('businessId', '==', businessId)
    );

    const unsubscribe = onSnapshot(chatQuery, async (snapshot) => {
      const msgs = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toDate() || new Date()
      })) as ChatMessage[];
      
      // Ordenar en memoria
      msgs.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
      
      setMessages(msgs);
      setLoading(false);

      // Marcar mensajes del admin como leídos
      const unreadFromAdmin = snapshot.docs.filter(
        docSnap => docSnap.data().senderRole === 'admin' && !docSnap.data().isRead
      );
      
      for (const msgDoc of unreadFromAdmin) {
        await updateDoc(doc(db, 'chats', msgDoc.id), { isRead: true });
      }
    }, (error) => {
      console.error('Error escuchando mensajes:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser?.id, businessId]);

  // Enviar mensaje
  const handleSend = async () => {
    if (!newMessage.trim() || !currentUser || !businessId) return;

    setSending(true);
    try {
      await addDoc(collection(db, 'chats'), {
        text: newMessage.trim(),
        senderId: currentUser.id,
        senderName: `${currentUser.firstName} ${currentUser.lastName}`,
        senderRole: 'user',
        businessId,
        participantIds: [currentUser.id],
        isRead: false,
        createdAt: serverTimestamp()
      });

      setNewMessage('');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Error enviando mensaje:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Hoy';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    } else {
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short'
      });
    }
  };

  // Agrupar mensajes por fecha
  const groupedMessages = messages.reduce((groups, message) => {
    const dateKey = message.createdAt.toDateString();
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(message);
    return groups;
  }, {} as Record<string, ChatMessage[]>);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Háblanos</title>
      </Helmet>

      <div className="flex flex-col h-[calc(100vh-8rem)] max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-gray-900 rounded-t-2xl p-4 border border-gray-800 border-b-0">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">
                {currentBusiness?.name || 'Soporte'}
              </h1>
              <p className="text-sm text-gray-400">
                Te responderemos lo antes posible
              </p>
            </div>
          </div>
        </div>

        {/* Messages area */}
        <div className="flex-1 bg-gray-950 border-x border-gray-800 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center mb-4">
                <MessageCircle className="w-10 h-10 text-gray-600" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                ¡Escríbenos!
              </h3>
              <p className="text-gray-500 max-w-sm">
                Estamos aquí para ayudarte. Envíanos un mensaje y te responderemos lo antes posible.
              </p>
            </div>
          ) : (
            Object.entries(groupedMessages).map(([dateKey, dayMessages]) => (
              <div key={dateKey}>
                {/* Date separator */}
                <div className="flex items-center justify-center my-4">
                  <span className="bg-gray-800 text-gray-400 text-xs px-3 py-1 rounded-full">
                    {formatDate(dayMessages[0].createdAt)}
                  </span>
                </div>

                {/* Messages for this date */}
                {dayMessages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex mb-3 ${
                      message.senderRole === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                        message.senderRole === 'user'
                          ? 'bg-primary-600 text-white rounded-br-md'
                          : 'bg-gray-800 text-white rounded-bl-md'
                      }`}
                    >
                      {message.senderRole === 'admin' && (
                        <p className="text-xs text-primary-400 font-medium mb-1">
                          {currentBusiness?.name || 'Admin'}
                        </p>
                      )}
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {message.text}
                      </p>
                      <p className={`text-xs mt-1 ${
                        message.senderRole === 'user' ? 'text-primary-200' : 'text-gray-500'
                      }`}>
                        {formatTime(message.createdAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="bg-gray-900 rounded-b-2xl p-4 border border-gray-800 border-t-0">
          <div className="flex items-center gap-3">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe un mensaje..."
              className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={sending}
            />
            <button
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="p-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-xl text-white transition-colors"
            >
              {sending ? (
                <Loading size="sm" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default HablanosPage;

